import * as d3 from 'd3';
import type { CommonsStats, CurvePoint } from '../lib/types';

function formatDelay(days: number) {
  if (days === 1) return '1d';
  if (days < 30) return `${days}d`;
  if (days < 365) return `${Math.round(days / 30)}m`;
  return '1y';
}

export function CurveChart({ points, commons }: { points: CurvePoint[]; commons?: CommonsStats | null }) {
  const width = 760;
  const height = 360;
  const margin = { top: 26, right: 32, bottom: 44, left: 52 };
  const x = d3.scaleLinear().domain([0, 365]).range([margin.left, width - margin.right]);
  const y = d3.scaleLinear().domain([0, 1]).range([height - margin.bottom, margin.top]);
  const own = [{ delayDays: 0, normalizedValue: 1, subjectiveValue: 100, kEstimate: 0, n: 1 }, ...points]
    .sort((a, b) => a.delayDays - b.delayDays);
  const ownLine = d3
    .line<(typeof own)[number]>()
    .x((d) => x(d.delayDays))
    .y((d) => y(d.normalizedValue))
    .curve(d3.curveMonotoneX)(own);

  const ownArea = d3
    .area<(typeof own)[number]>()
    .x((d) => x(d.delayDays))
    .y0(y(0))
    .y1((d) => y(d.normalizedValue))
    .curve(d3.curveMonotoneX)(own);

  const commonsPoints = commons?.bins?.length
    ? [{ delayDays: 0, meanValue: 1 }, ...commons.bins.map((b) => ({ delayDays: b.delayDays, meanValue: b.meanValue }))]
    : [];
  const commonsLine = commonsPoints.length
    ? d3
        .line<(typeof commonsPoints)[number]>()
        .x((d) => x(d.delayDays))
        .y((d) => y(d.meanValue))
        .curve(d3.curveMonotoneX)(commonsPoints)
    : '';

  const xTicks = [0, 1, 7, 30, 90, 180, 365];
  const yTicks = [0, 0.25, 0.5, 0.75, 1];

  return (
    <svg className="curve-chart" viewBox={`0 0 ${width} ${height}`} role="img" aria-labelledby="curve-title curve-desc">
      <title id="curve-title">Estimated hyperbolic discount curve</title>
      <desc id="curve-desc">
        A line chart showing subjective present value of a delayed reward as delay increases from now to one year.
      </desc>
      <defs>
        <linearGradient id="areaGlow" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="rgba(126, 231, 255, 0.42)" />
          <stop offset="1" stopColor="rgba(126, 231, 255, 0.03)" />
        </linearGradient>
        <filter id="softGlow" x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="4" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      <rect x="0" y="0" width={width} height={height} rx="28" className="chart-backdrop" />
      {yTicks.map((tick) => (
        <g key={tick}>
          <line x1={margin.left} x2={width - margin.right} y1={y(tick)} y2={y(tick)} className="grid-line" />
          <text x={margin.left - 12} y={y(tick) + 4} textAnchor="end" className="axis-label">
            {Math.round(tick * 100)}
          </text>
        </g>
      ))}
      {xTicks.map((tick) => (
        <g key={tick}>
          <line x1={x(tick)} x2={x(tick)} y1={margin.top} y2={height - margin.bottom} className="grid-line vertical" />
          <text x={x(tick)} y={height - 16} textAnchor="middle" className="axis-label">
            {tick === 0 ? 'now' : formatDelay(tick)}
          </text>
        </g>
      ))}

      <text x={margin.left} y="22" className="chart-kicker">subjective value of 100 later-coins</text>
      <text x={width - margin.right} y="22" textAnchor="end" className="chart-kicker">delay horizon</text>

      {commonsLine ? <path d={commonsLine ?? ''} className="commons-line" /> : null}
      {ownArea ? <path d={ownArea} fill="url(#areaGlow)" /> : null}
      {ownLine ? <path d={ownLine} className="own-line" filter="url(#softGlow)" /> : null}
      {own.map((point) => (
        <g key={`${point.delayDays}-${point.normalizedValue}`}>
          <circle cx={x(point.delayDays)} cy={y(point.normalizedValue)} r={point.delayDays === 0 ? 4 : 6} className="curve-dot" />
          {point.delayDays > 0 && point.n > 0 ? (
            <text x={x(point.delayDays)} y={y(point.normalizedValue) - 12} textAnchor="middle" className="dot-label">
              {Math.round(point.normalizedValue * 100)}
            </text>
          ) : null}
        </g>
      ))}
    </svg>
  );
}
